/**
 * Container for Footer
 *
 * @module components/Footer
 */

import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import PrivacyOptionsIconSvg from '../../../static/icons/privacyoptions.svg';
import * as Actions from '../../actions';
import ButtonT3 from '../../components/lib/ButtonT3/ButtonT3';
import { ButtonT3Theme } from '../../components/lib/ButtonT3/constants';
import { AriaRoles } from '../../constants/AccessibilityConstants';
import * as BusinessesConstants from '../../constants/Businesses';
import * as CommunityConstants from '../../constants/Community';
import * as FooterConstants from '../../constants/Footer';
import { StatsOnboardingActions } from '../../constants/Onboarding';
import QATags from '../../constants/QATags';
import {
  getCCPALinkUrl,
  getIsPrivacyLawComplianceEnabled,
  isCCPALinkEnabled,
} from '../../lib/configuration/TunerVarUtils';
import { getRegisterPath } from '../../lib/RouteUtils';
import { isTunerBarOpen } from '../../lib/UIStateUtils';
import { createBEM } from '../../lib/utils/css';
import Icon from '../Icon/Icon';

import './Footer.scss';

export class Footer extends React.Component {
  static propTypes = {
    CCPALinkUrl: PropTypes.string,
    doesSignUpSectionOverlap: PropTypes.bool,
    hasSignUpSection: PropTypes.bool,
    linkClicked: PropTypes.func,
    navigateToBusinesses: PropTypes.func,
    navigateToCommunity: PropTypes.func,
    signUpClicked: PropTypes.func,
    useLightTheme: PropTypes.bool,
    willInvertThemeAtXS: PropTypes.bool,

    isBackgroundBlack: PropTypes.bool,
    isCCPALinkEnabled: PropTypes.bool,
    isNowPlaying: PropTypes.bool,
    isPrivacyLawComplianceEnabled: PropTypes.bool,
    isTunerOpen: PropTypes.bool.isRequired,
    isUpgradePage: PropTypes.bool,
  };

  renderFooterNavLinks = () => {
    return FooterConstants.footerNavLinks
      .map(([text, href], index) => {
        let updatedHref;
        if (text === FooterConstants.FooterMessages.businesses) {
          return this.renderNavLink({
            index,
            text,
            href,
            onClick: () => {
              this.props.navigateToBusinesses(true, {
                action: BusinessesConstants.EventAction.BUSINESSES_CLICKED_FROM_FOOTER,
              });
            },
          });
        }

        if (text === FooterConstants.FooterMessages.community) {
          return this.renderNavLink({
            index,
            text,
            onClick: () =>
              this.props.navigateToCommunity(true, {
                action: CommunityConstants.EventAction.COMMUNITY_CLICKED_FROM_FOOTER,
              }),
          });
        }

        if (text === FooterConstants.FooterMessages.doNotSell) {
          if (this.props.isCCPALinkEnabled && !this.props.isPrivacyLawComplianceEnabled) {
            updatedHref = this.props.CCPALinkUrl;
          } else {
            return;
          }
        }

        if (text === FooterConstants.FooterMessages.yourPrivacyChoices) {
          if (!this.props.isPrivacyLawComplianceEnabled) {
            return;
          }
          const icon = PrivacyOptionsIconSvg;

          return this.renderNavLink({ index, text, href: updatedHref || href, icon });
        }

        if (text === FooterConstants.FooterMessages.cookieSettings) {
          if (this.props.isPrivacyLawComplianceEnabled) {
            return this.renderNavLink({
              text,
              index,
              onClick() {
                // WEBCA-2689 - Hack to get the OneTrust link to work
                const fLink = document.getElementById('onetrusthack');
                fLink.click();
              },
            });
          }
          return;
        }

        return this.renderNavLink({ index, text, href: updatedHref || href });
      })
      .filter(el => el);
  };

  renderNavLink = ({ index, text, href, onClick, className, icon }) => {
    const props = {
      className: classNames('Footer__navLink', className),
      href,
      key: index,
      onClick,
      target: '_blank',
    };

    return (
      <a {...props}>
        <FormattedMessage {...text} />
        {icon && <Icon className='Footer__navLink__icon' glyph={icon} />}
      </a>
    );
  };

  logEvent = () => {
    if (this.props.isUpgradePage) {
      this.props.linkClicked(StatsOnboardingActions.CLICK_SIGN_UP_LANDING);
    }
    this.props.signUpClicked();
  };

  renderSignUpButton = () => {
    return (
      <div className='Footer__signUpCTA'>
        <ButtonT3
          theme={ButtonT3Theme.DARK}
          href={getRegisterPath()}
          qaTag={QATags.SUCTA}
          onClick={this.logEvent}
        >
          <FormattedMessage {...FooterConstants.FooterMessages.signUpNow} />
        </ButtonT3>
      </div>
    );
  };

  renderSignUpSection = () => {
    const signUpSectionClasses = createBEM('Footer__signUp', {
      overlap: this.props.doesSignUpSectionOverlap,
    });

    return (
      <div className={signUpSectionClasses}>
        <p className='Footer__signUpMessage' data-qa={QATags.SUM}>
          <FormattedMessage {...FooterConstants.FooterMessages.signUp} />
        </p>
        {this.renderSignUpButton()}
      </div>
    );
  };

  render() {
    const footerClasses = createBEM('Footer', {
      blackBackground: this.props.isBackgroundBlack || this.props.hasSignUpSection,
      invertThemeAtXS: this.props.willInvertThemeAtXS,
      light: this.props.useLightTheme,
      nowPlaying: this.props.isNowPlaying,
      withTuner: this.props.isTunerOpen,
    });

    return (
      <div className={footerClasses}>
        {this.props.hasSignUpSection && this.renderSignUpSection()}
        <footer className='Footer__content' data-qa={QATags.F} role={AriaRoles.CONTENTINFO}>
          <nav className='Footer__nav'>{this.renderFooterNavLinks()}</nav>
          <div className='Footer__copyright'>
            <FormattedMessage
              {...FooterConstants.FooterMessages.copyright}
              values={{ date: new Date().getFullYear() }}
            />
          </div>
        </footer>
      </div>
    );
  }
}

export function mapStateToProps(state) {
  return {
    isTunerOpen: isTunerBarOpen(state),
    isCCPALinkEnabled: isCCPALinkEnabled(state),
    CCPALinkUrl: getCCPALinkUrl(state),
    isPrivacyLawComplianceEnabled: getIsPrivacyLawComplianceEnabled(state),
  };
}

const mapDispatchToProps = {
  linkClicked: Actions.UseCases.linkClicked,
  navigateToBusinesses: Actions.UseCases.navigateToBusinesses,
  navigateToCommunity: Actions.UseCases.navigateToCommunity,
  signUpClicked: Actions.Integrations.signUpClicked,
};

export default connect(mapStateToProps, mapDispatchToProps)(Footer);
